import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, MapPin, ChevronDown } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { t, COUNTRIES, LANGS } from "../lib/i18n";
import { toast } from "sonner";

const STEPS = ["basics", "location"];
const CITY_SUGGESTIONS = {
  ru: [
    "Москва", "Санкт-Петербург", "Новосибирск", "Екатеринбург", "Казань",
    "Нижний Новгород", "Челябинск", "Самара", "Омск", "Ростов-на-Дону",
    "Уфа", "Красноярск", "Воронеж", "Пермь", "Волгоград",
  ],
  kz: [
    "Алматы", "Астана", "Шымкент", "Караганда", "Актобе",
    "Тараз", "Павлодар", "Усть-Каменогорск", "Семей", "Костанай",
    "Кызылорда", "Уральск", "Атырау", "Актау", "Петропавловск",
  ],
  by: [
    "Минск", "Гомель", "Могилев", "Витебск", "Гродно",
    "Брест", "Бобруйск", "Барановичи", "Пинск", "Орша",
    "Мозырь", "Лида", "Солигорск", "Новополоцк", "Полоцк",
  ],
  uz: [
    "Ташкент", "Самарканд", "Бухара", "Наманган", "Андижан",
    "Фергана", "Нукус", "Карши", "Маргилан", "Коканд",
    "Ургенч", "Термез", "Навои", "Джизак", "Гулистан",
  ],
  kg: [
    "Бишкек", "Ош", "Каракол", "Джалал-Абад", "Токмок",
    "Балыкчы", "Кара-Балта", "Узген", "Нарын", "Талас",
    "Кант", "Баткен", "Исфана", "Сулюкта", "Кызыл-Кия",
  ],
};

function calcAgeFromDate(raw) {
  if (!raw) return null;
  const [y, m, d] = raw.split("-").map(Number);
  if (!y || !m || !d) return null;
  const today = new Date();
  let age = today.getFullYear() - y;
  const monthDiff = today.getMonth() - (m - 1);
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < d)) age -= 1;
  return age;
}

function localDateString(y, m, d) {
  const mm = String(m).padStart(2, "0");
  const dd = String(d).padStart(2, "0");
  return `${y}-${mm}-${dd}`;
}

function birthDateFromAge(age) {
  const today = new Date();
  return localDateString(today.getFullYear() - age, today.getMonth() + 1, today.getDate());
}

function zodiacFromDate(raw) {
  if (!raw) return "";
  const parts = raw.split("-");
  const month = parseInt(parts[1], 10);
  const day = parseInt(parts[2], 10);
  if (!month || !day) return "";
  if ((month === 3 && day >= 21) || (month === 4 && day <= 19)) return "Овен";
  if ((month === 4 && day >= 20) || (month === 5 && day <= 20)) return "Телец";
  if ((month === 5 && day >= 21) || (month === 6 && day <= 20)) return "Близнецы";
  if ((month === 6 && day >= 21) || (month === 7 && day <= 22)) return "Рак";
  if ((month === 7 && day >= 23) || (month === 8 && day <= 22)) return "Лев";
  if ((month === 8 && day >= 23) || (month === 9 && day <= 22)) return "Дева";
  if ((month === 9 && day >= 23) || (month === 10 && day <= 22)) return "Весы";
  if ((month === 10 && day >= 23) || (month === 11 && day <= 21)) return "Скорпион";
  if ((month === 11 && day >= 22) || (month === 12 && day <= 21)) return "Стрелец";
  if ((month === 12 && day >= 22) || (month === 1 && day <= 19)) return "Козерог";
  if ((month === 1 && day >= 20) || (month === 2 && day <= 18)) return "Водолей";
  return "Рыбы";
}

export default function Onboarding() {
  const { user, setUser, loginTelegram, refresh } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    first_name: user?.first_name || "",
    birth_date: user?.birth_date || "",
    gender: user?.gender || "male",
    looking_for: user?.looking_for || "any",
    height_cm: user?.height_cm || "",
    worldview: user?.worldview || "",
    education: user?.education || "",
    children: user?.children || "",
    spoken_languages: user?.spoken_languages || [],
    alcohol: user?.alcohol || "",
    smoking: user?.smoking || "",
    work_field: user?.work_field || "",
    preferences: user?.preferences || "",
    seeking: user?.seeking || [],
    country: user?.country || "ru",
    city: user?.city || "",
    language: user?.language || "ru",
    bio: user?.bio || "",
    latitude: null,
    longitude: null,
  });
  const lang = form?.language || user?.language || "ru";
  const [busy, setBusy] = useState(false);
  const [locStatus, setLocStatus] = useState("idle"); // idle | asking | granted | denied

  useEffect(() => {
    // Basics already saved (birth_date is only ever set by this step) but no
    // photo yet — skip straight to the photo step instead of re-asking basics.
    if (user?.birth_date && user?.photos?.length === 0) {
      nav("/photos", { replace: true });
    }
  }, [user?.birth_date, user?.photos?.length, nav]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const askLocation = () => {
    if (!navigator.geolocation) {
      setLocStatus("denied");
      toast.error(t(lang, "geo_unavailable"));
      return;
    }
    setLocStatus("asking");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        update({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
        setLocStatus("granted");
      },
      () => {
        setLocStatus("denied");
        toast(t(lang, "geo_skip"));
      },
      { timeout: 8000 }
    );
  };

  const next = () => {
    if (step === 0) {
      if (!form.first_name.trim()) return toast.error(t(lang, "name_required"));
      const age = calcAgeFromDate(form.birth_date);
      if (!age) return toast.error("Укажите дату рождения");
      if (age < 18 || age > 100) return toast.error(t(lang, "age_range"));
    }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };
  const prev = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    if (!form.city.trim()) return toast.error(t(lang, "city_required"));
    setBusy(true);
    const payload = {
      ...form,
      height_cm: form.height_cm === "" ? null : form.height_cm,
      spoken_languages: form.spoken_languages || [],
      preferences: (form.preferences || "").trim(),
      seeking: form.seeking || [],
    };
    try {
      await api.post("/profile/onboard", payload);
      await refresh();
      toast.success(t(lang, "profile_created"));
      // useEffect will handle navigation when user.birth_date becomes set
    } catch (e) {
      if (e?.response?.status === 401) {
        try {
          await loginTelegram();
          await api.post("/profile/onboard", payload);
          await refresh();
          toast.success(t(lang, "profile_created"));
          // useEffect will handle navigation
          return;
        } catch {}
      }
      toast.error(e.response?.data?.detail || t(lang, "error_generic"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="kupidon-shell safe-top safe-bottom flex flex-col relative">
      <div className="flex items-center gap-3 px-5 pt-4">
        {step > 0 ? (
          <button data-testid="onboarding-back" onClick={prev} className="p-2 -ml-2 rounded-full hover:bg-white/50">
            <ChevronLeft className="w-6 h-6" />
          </button>
        ) : (
          <div className="w-6" />
        )}
        <div className="flex-1 h-1.5 bg-white/60 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-[color:var(--peach)]"
            initial={false}
            animate={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
            transition={{ duration: 0.4 }}
          />
        </div>
        <div className="text-xs font-medium text-[color:var(--ink-soft)] w-10 text-right">
          {step + 1}/{STEPS.length}
        </div>
      </div>

      <div className="flex-1 px-6 py-8 overflow-y-auto no-scrollbar">
        {step === 0 && (
          <motion.div
            key="basics"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
            data-testid="onboarding-step-basics"
          >
            <h1 className="font-[Onest] font-extrabold text-4xl tracking-tight">{t(lang, "onboard_title")}</h1>
            <p className="text-[color:var(--ink-soft)] text-sm -mt-3">{t(lang, "onboard_hint")}</p>

            <div className="block">
              <span className="text-sm font-medium">{t(lang, "first_name")}</span>
              <input
                data-testid="onboarding-first-name"
                value={form.first_name}
                onChange={(e) => update({ first_name: e.target.value })}
                className="mt-1 w-full px-4 py-3 rounded-2xl bg-white/80 border border-[color:var(--border)] focus:outline-none focus:ring-2 focus:ring-[color:var(--peach)]"
                placeholder={t(lang, "first_name_ph")}
              />
            </div>

            <div className="block">
              <span className="text-sm font-medium block mb-3">Дата рождения</span>
              
              <div className="rounded-3xl bg-gradient-to-br from-[color:var(--peach-bg)] to-white border border-[color:var(--border)] p-5 space-y-4">
                {/* 3-column date picker */}
                <div className="flex gap-2 items-stretch">
                  {/* Day */}
                  <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-wider text-[color:var(--ink-soft)] mb-1.5 text-center font-medium">День</div>
                    <div className="relative">
                      <select
                        value={form.birth_date ? parseInt(form.birth_date.split("-")[2], 10) : ""}
                        onChange={(e) => {
                          const day = e.target.value;
                          if (!day) return;
                          const cur = form.birth_date ? new Date(form.birth_date) : new Date();
                          const month = form.birth_date ? parseInt(form.birth_date.split("-")[1], 10) - 1 : cur.getMonth();
                          const year = form.birth_date ? parseInt(form.birth_date.split("-")[0], 10) : cur.getFullYear();
                          const d = new Date(year, month, parseInt(day, 10));
                          if (d.getDate() === parseInt(day, 10)) {
                            update({ birth_date: localDateString(year, month + 1, parseInt(day, 10)) });
                          }
                        }}
                        className="w-full appearance-none px-3 py-3.5 rounded-2xl bg-white border border-[color:var(--border)] text-center text-sm font-semibold text-[color:var(--ink)] focus:outline-none focus:ring-2 focus:ring-[color:var(--peach)] cursor-pointer"
                      >
                        <option value="">—</option>
                        {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[color:var(--ink-soft)] pointer-events-none" />
                    </div>
                  </div>
                  {/* Month */}
                  <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-wider text-[color:var(--ink-soft)] mb-1.5 text-center font-medium">Месяц</div>
                    <div className="relative">
                      <select
                        value={form.birth_date ? parseInt(form.birth_date.split("-")[1], 10) : ""}
                        onChange={(e) => {
                          const month = e.target.value;
                          if (!month) return;
                          const cur = form.birth_date ? new Date(form.birth_date) : new Date();
                          const day = form.birth_date ? parseInt(form.birth_date.split("-")[2], 10) : cur.getDate();
                          const year = form.birth_date ? parseInt(form.birth_date.split("-")[0], 10) : cur.getFullYear();
                          const d = new Date(year, parseInt(month, 10) - 1, Math.min(day, 28));
                          update({ birth_date: localDateString(year, parseInt(month, 10), Math.min(day, 28)) });
                        }}
                        className="w-full appearance-none px-2 py-3.5 rounded-2xl bg-white border border-[color:var(--border)] text-center text-sm font-semibold text-[color:var(--ink)] focus:outline-none focus:ring-2 focus:ring-[color:var(--peach)] cursor-pointer"
                      >
                        <option value="">—</option>
                        {[
                          { v: 1, l: "Янв" }, { v: 2, l: "Фев" }, { v: 3, l: "Мар" },
                          { v: 4, l: "Апр" }, { v: 5, l: "Май" }, { v: 6, l: "Июн" },
                          { v: 7, l: "Июл" }, { v: 8, l: "Авг" }, { v: 9, l: "Сен" },
                          { v: 10, l: "Окт" }, { v: 11, l: "Ноя" }, { v: 12, l: "Дек" },
                        ].map((m) => (
                          <option key={m.v} value={m.v}>{m.l}</option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[color:var(--ink-soft)] pointer-events-none" />
                    </div>
                  </div>
                  {/* Year */}
                  <div className="flex-[1.3]">
                    <div className="text-[10px] uppercase tracking-wider text-[color:var(--ink-soft)] mb-1.5 text-center font-medium">Год</div>
                    <div className="relative">
                      <select
                        value={form.birth_date ? parseInt(form.birth_date.split("-")[0], 10) : ""}
                        onChange={(e) => {
                          const year = e.target.value;
                          if (!year) return;
                          const cur = form.birth_date ? new Date(form.birth_date) : new Date();
                          const day = form.birth_date ? Math.min(parseInt(form.birth_date.split("-")[2], 10), 28) : 1;
                          const month = form.birth_date ? parseInt(form.birth_date.split("-")[1], 10) - 1 : 0;
                          const d = new Date(parseInt(year, 10), month, day);
                          update({ birth_date: localDateString(parseInt(year, 10), month + 1, day) });
                        }}
                        className="w-full appearance-none px-2 py-3.5 rounded-2xl bg-white border border-[color:var(--border)] text-center text-sm font-semibold text-[color:var(--ink)] focus:outline-none focus:ring-2 focus:ring-[color:var(--peach)] cursor-pointer"
                      >
                        <option value="">—</option>
                        {Array.from({ length: 83 }, (_, i) => new Date().getFullYear() - 18 - i).map((y) => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[color:var(--ink-soft)] pointer-events-none" />
                    </div>
                  </div>
                </div>

                {/* Age display + zodiac */}
                <div className="flex items-center justify-between pt-2 border-t border-[color:var(--border)]">
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-[color:var(--ink-soft)]">Возраст</span>
                    <span className="text-xl font-[Onest] font-bold text-[color:var(--peach)]">
                      {calcAgeFromDate(form.birth_date) || "—"}
                    </span>
                    {calcAgeFromDate(form.birth_date) && (
                      <span className="text-xs text-[color:var(--ink-soft)]">лет</span>
                    )}
                  </div>
                  {form.birth_date && (
                    <span className="px-3 py-1.5 rounded-full bg-white border border-[color:var(--peach)] text-[color:var(--peach)] text-xs font-medium">
                      {zodiacFromDate(form.birth_date)}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div>
              <span className="text-sm font-medium">{t(lang, "i_am")}</span>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {[
                  { v: "male", l: t(lang, "male") },
                  { v: "female", l: t(lang, "female") },
                ].map((o) => (
                  <button
                    key={o.v}
                    data-testid={`onboarding-gender-${o.v}`}
                    onClick={() => update({ gender: o.v })}
                    className={`btn-pill text-sm ${
                      form.gender === o.v
                        ? "bg-[color:var(--peach)] text-white"
                        : "bg-white/70 text-[color:var(--ink)] border border-[color:var(--border)]"
                    }`}
                  >
                    {o.l}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <span className="text-sm font-medium">{t(lang, "looking_for")}</span>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {[
                  { v: "male", l: t(lang, "guys") },
                  { v: "female", l: t(lang, "girls") },
                  { v: "any", l: t(lang, "any") },
                ].map((o) => (
                  <button
                    key={o.v}
                    data-testid={`onboarding-lookingfor-${o.v}`}
                    onClick={() => update({ looking_for: o.v })}
                    className={`btn-pill text-sm ${
                      form.looking_for === o.v
                        ? "bg-[color:var(--ink)] text-white"
                        : "bg-white/70 text-[color:var(--ink)] border border-[color:var(--border)]"
                    }`}
                  >
                    {o.l}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {step === 1 && (
          <motion.div
            key="loc"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
            data-testid="onboarding-step-location"
          >
            <h1 className="font-[Onest] font-extrabold text-4xl tracking-tight">{t(lang, "where")}</h1>
            <p className="text-[color:var(--ink-soft)] text-sm -mt-3">
              {t(lang, "where_hint")}
            </p>

            <div>
              <span className="text-sm font-medium">{t(lang, "country")}</span>
              <select
                data-testid="onboarding-country"
                value={form.country}
                onChange={(e) => update({ country: e.target.value })}
                className="mt-1 w-full px-4 py-3 rounded-2xl bg-white/80 border border-[color:var(--border)] focus:outline-none focus:ring-2 focus:ring-[color:var(--peach)]"
              >
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="block">
              <span className="text-sm font-medium">{t(lang, "city")}</span>
              <input
                data-testid="onboarding-city"
                value={form.city}
                onChange={(e) => update({ city: e.target.value })}
                className="mt-1 w-full px-4 py-3 rounded-2xl bg-white/80 border border-[color:var(--border)] focus:outline-none focus:ring-2 focus:ring-[color:var(--peach)]"
                placeholder={t(lang, "city_ph")}
              />
              <div className="mt-2 flex flex-wrap gap-2">
                {(CITY_SUGGESTIONS[form.country] || CITY_SUGGESTIONS.ru).map((city) => (
                  <button
                    key={city}
                    type="button"
                    onClick={() => update({ city })}
                    className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                      form.city === city
                        ? "bg-[color:var(--peach)] text-white border-[color:var(--peach)]"
                        : "bg-white/70 text-[color:var(--ink)] border-[color:var(--border)] hover:bg-white"
                    }`}
                  >
                    {city}
                  </button>
                ))}
              </div>
              <p className="mt-2 text-xs text-[color:var(--ink-soft)]">
                {lang === "ru"
                  ? "Выберите из подсказок или укажите свой город вручную."
                  : "Pick a suggestion or type your city manually."}
              </p>
            </div>

            <div>
              <span className="text-sm font-medium">{t(lang, "language")}</span>
              <select
                data-testid="onboarding-language"
                value={form.language}
                onChange={(e) => update({ language: e.target.value })}
                className="mt-1 w-full px-4 py-3 rounded-2xl bg-white/80 border border-[color:var(--border)] focus:outline-none focus:ring-2 focus:ring-[color:var(--peach)]"
              >
                {LANGS.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.flag} {c.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="rounded-3xl bg-white/70 border border-[color:var(--border)] p-5 flex items-start gap-3">
              <div className="w-11 h-11 rounded-2xl bg-[color:var(--peach)]/15 flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-[color:var(--peach-deep)]" />
              </div>
              <div className="flex-1">
                <div className="font-semibold text-sm">{t(lang, "location_title")}</div>
                <div className="text-xs text-[color:var(--ink-soft)] mt-1">
                  {locStatus === "granted"
                    ? t(lang, "location_granted")
                    : t(lang, "location_ask")}
                </div>
                {locStatus !== "granted" && (
                  <button
                    data-testid="onboarding-request-location"
                    onClick={askLocation}
                    className="btn-pill mt-3 text-sm bg-[color:var(--peach)] text-white px-4 py-2"
                  >
                    {locStatus === "asking" ? t(lang, "location_asking") : t(lang, "location_allow")}
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        )}

      </div>

      <div className="px-6 pb-6">
        {step < STEPS.length - 1 ? (
          <button
            data-testid="onboarding-next"
            onClick={next}
            className="btn-pill w-full bg-[color:var(--ink)] text-white"
          >
            {t(lang, "next")}
          </button>
        ) : (
          <button
            data-testid="onboarding-finish"
            onClick={submit}
            disabled={busy}
            className="btn-pill w-full bg-[color:var(--peach)] text-white disabled:opacity-60"
          >
            {busy ? t(lang, "saving") : t(lang, "finish")}
          </button>
        )}
      </div>
    </div>
  );
}
